import React, { useState, useEffect } from 'react';
import { Modal, Row, Col, Tooltip, Divider, Tabs, Segmented, DatePicker } from 'antd';
import { AreaChartOutlined, HistoryOutlined } from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import moment from 'moment';
import {
  formatWholeNumber,
  formatNumber2SigFig,
  formatCurrency2SigFig,
  isEndedCampaign,
  computeEndedPacing,
} from '../../../../core/utils/campaigns.js';
import { PacingDot, PacingBar, BulletBar, RateBars, CumulativePacingChart, DailyDeliveryChart, BudgetDonut, getAllTimeOnPaceRatio, getAllTimeProjection, getAllTimeTone, getDeliveryRatePerDay, getPacingTone, PACING_TONES, TREND_ARROWS } from '../shared/pacingVisuals';
import { getEffectiveCpm } from '../../../../core/utils/campaigns';

const { TabPane } = Tabs;

const labelStyle = {
  fontSize: '13px',
  color: '#8c8c8c',
};

const valueStyle = {
  fontSize: '13px',
  fontWeight: 600,
  color: '#262626',
};

const sectionTitleStyle = {
  fontSize: '14px',
  fontWeight: 600,
  color: '#434343',
  marginBottom: '12px',
  display: 'block',
};

const DetailRow = ({ label, value, tooltip }) => (
  <Row
    style={{
      padding: '8px 0',
      borderBottom: '1px solid #f5f5f5',
    }}
    type="flex"
    justify="space-between"
    align="middle"
  >
    <Col>
      {tooltip ? (
        <Tooltip title={tooltip}>
          <Text style={labelStyle}>{label}</Text>
        </Tooltip>
      ) : (
        <Text style={labelStyle}>{label}</Text>
      )}
    </Col>
    <Col>
      <Text style={valueStyle}>{value}</Text>
    </Col>
  </Row>
);

// Ended-campaign views. The consolidate pipeline doesn't reprocess ended
// campaigns, so `pacingData` goes stale — for ended campaigns we render
// from `computeEndedPacing(row)`, which sources from declared budget/goal
// and delivered totals (no projection). Current and All Time tabs show the
// same data here (an ended campaign has no "today vs. lifetime" distinction).
const PercentageEnded = ({ ended }) => (
  <>
    <Text style={sectionTitleStyle}>Final Budget Pacing</Text>
    <DetailRow
      label="Pace"
      value={
        <span style={{ color: getPaceColor(ended.budgetPace, true) }}>
          {formatNumber2SigFig(ended.budgetPace)}%
        </span>
      }
      tooltip="Final pace — Total Budget Spent / Total Budget"
    />
    <DetailRow
      label={ended.budgetLeft < 0 ? 'Overage' : 'Remaining'}
      value={formatCurrency2SigFig(ended.budgetLeft)}
    />
    <DetailRow
      label="Total Budget Spent"
      value={formatCurrency2SigFig(ended.budgetSpent)}
    />
    <DetailRow
      label="Total Budget"
      value={formatCurrency2SigFig(ended.budget)}
    />
  </>
);

const ImpressionEnded = ({ ended }) => (
  <>
    <Text style={sectionTitleStyle}>Final Impression Pacing</Text>
    <DetailRow
      label="Pace"
      value={
        <span style={{ color: getPaceColor(ended.impressionPace, false) }}>
          {formatNumber2SigFig(ended.impressionPace)}%
        </span>
      }
      tooltip="Final pace — Served Impressions / Impression Goal"
    />
    <DetailRow
      label={ended.impressionsLeft < 0 ? 'Overage' : 'Remaining'}
      value={formatWholeNumber(ended.impressionsLeft)}
    />
    <DetailRow
      label="Total Served Impressions"
      value={formatWholeNumber(ended.impressionsServed)}
    />
    <DetailRow
      label="Impression Goal"
      value={formatWholeNumber(ended.impressionGoal)}
    />
  </>
);

// Visual summary for the new-view tab bodies: goal donut (served vs
// remaining with the % in the hole), key stats with a today-vs-goal
// chip, and the daily delivery chart against the goal line. Classic
// view skips this entirely.
const TabVisualSummary = ({
  served,
  goal,
  dailyActual,
  dailyGoal,
  dailyLabel,
  byDate,
  highlightFromEnd,
  tone,
  chartValueKey = 'impressions',
  formatValue,
}) => {
  const servedPct = goal > 0 ? (served / goal) * 100 : 0;
  const dailyRatio = dailyGoal > 0 ? (dailyActual / dailyGoal) * 100 : null;
  const chipTone =
    dailyRatio === null
      ? PACING_TONES.neutral
      : dailyRatio >= 100
        ? PACING_TONES.onTrack
        : dailyRatio >= 75
          ? PACING_TONES.watch
          : PACING_TONES.behind;
  return (
    <div style={{ marginBottom: 12, marginTop: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <Tooltip
          title={`${formatValue(served)} of ${formatValue(goal)} delivered (${formatNumber2SigFig(servedPct)}%)`}
        >
          <BudgetDonut spentPct={servedPct} tone={tone} size={76} />
        </Tooltip>
        <div>
          <Text style={{ fontSize: 13, display: 'inline-flex', alignItems: 'center' }}>
            <span style={{ color: '#595959' }}>{dailyLabel}:</span>{' '}
            <span
              style={{
                fontWeight: 600,
                fontVariantNumeric: 'tabular-nums',
                color: PACING_TONES.ink,
              }}
            >
              {formatValue(dailyActual)} / {formatValue(dailyGoal)}
            </span>
            {dailyRatio !== null && (
              <span
                style={{
                  marginLeft: 6,
                  display: 'inline-flex',
                  alignItems: 'center',
                  color: PACING_TONES.ink,
                }}
              >
                <PacingDot tone={chipTone} />
                {formatNumber2SigFig(dailyRatio)}%
              </span>
            )}
          </Text>
          <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
            Remaining {formatValue(goal - served)} · goal {formatValue(goal)}
          </Text>
        </div>
      </div>
      <div style={{ marginTop: 8 }}>
        <DailyDeliveryChart
          byDate={byDate}
          dailyGoal={dailyGoal}
          highlightFromEnd={highlightFromEnd}
          tone={tone}
          valueKey={chartValueKey}
        />
      </div>
    </div>
  );
};

const PercentageCurrent = ({ pacingData, row, status, switchPacingYesterday, paceAdditionalInfo, classicPacing }) => {
  const todayEntry = paceAdditionalInfo?.[0];
  const yesterdayEntry = paceAdditionalInfo?.[1];
  const source = switchPacingYesterday ? yesterdayEntry : todayEntry;

  let pace = source?.pace ?? 0;
  let budgetSpent = source?.budgetSpent ?? 0;
  let budgetSpentToday = source?.budgetSpentToday ?? 0;
  let budgetLeft = source?.budgetLeft ?? 0;
  let dailyAvgBudgetLeft = source?.dailyAvgBudgetLeft ?? 0;
  const totalBudget = source?.totalBudget ?? pacingData?.overallBudget ?? 0;

  if (status === 'FINISHED') {
    dailyAvgBudgetLeft = 0;
    budgetLeft = totalBudget - budgetSpent;
    budgetSpentToday = 0;
    pace = todayEntry?.pace ?? pacingData?.pace ?? 0;
  }

  const paceColor = classicPacing ? getPaceColor(pace, true) : getPacingTone(pace, { isPercentage: true });

  return (
    <>
      <Text style={sectionTitleStyle}>Current Budget Pacing</Text>

      {!classicPacing && status !== 'FINISHED' && (
        <TabVisualSummary
          served={budgetSpent}
          goal={totalBudget}
          dailyActual={budgetSpentToday ?? 0}
          dailyGoal={dailyAvgBudgetLeft}
          dailyLabel={switchPacingYesterday ? 'Spent yesterday' : 'Spent today'}
          byDate={row?.aggregateData?.byDate}
          highlightFromEnd={switchPacingYesterday ? 1 : 0}
          tone={paceColor}
          chartValueKey="totalCost"
          formatValue={(v) => formatCurrency2SigFig(v || 0)}
        />
      )}

      <DetailRow
        label="Pace"
        value={<span style={{ color: paceColor }}>{formatNumber2SigFig(pace)}%</span>}
        tooltip="Pacing for Percentage pricing model"
      />
      <DetailRow
        label="Remaining"
        value={formatCurrency2SigFig(budgetLeft)}
      />
      <DetailRow
        label="Expected Daily"
        value={formatCurrency2SigFig(dailyAvgBudgetLeft)}
        tooltip="Daily goal"
      />
      <DetailRow
        label="Spend"
        value={formatCurrency2SigFig(budgetSpentToday ?? 0)}
        tooltip="Amount spent within the day"
      />
      <DetailRow
        label="Total Budget Spent"
        value={formatCurrency2SigFig(budgetSpent)}
        tooltip="Cumulative budget spent as of the selected day"
      />
      <DetailRow
        label="Total Budget"
        value={formatCurrency2SigFig(totalBudget)}
        tooltip="Total Budget for the current pacing period"
      />
    </>
  );
};

const PercentageAllTime = ({ pacingData, row, switchPacingYesterday, paceAdditionalInfo, classicPacing, projectToDate }) => {
  const allTime = getAllTimeEntry(paceAdditionalInfo);
  const totalBudget = allTime?.totalBudget ?? pacingData?.overallBudget ?? 0;
  const totalBudgetSpent = allTime?.budgetSpent ?? pacingData?.overallSpend ?? 0;
  const remaining = allTime?.budgetLeft ?? (totalBudget - totalBudgetSpent);
  const expectedDaily =
    allTime?.dailyAvgBudget ??
    (() => {
      const flightDays = getFlightDays(row);
      return flightDays > 0 ? totalBudget / flightDays : 0;
    })();
  // New view: transparent spent-vs-expected-to-date ratio (same formula
  // as the hero); classic keeps the legacy backend number.
  const onPaceRatio = classicPacing
    ? null
    : getAllTimeOnPaceRatio(
        totalBudget,
        totalBudgetSpent,
        allTime?.daysIntoFlight,
        allTime?.daysLeft
      );
  const pace = onPaceRatio ?? (allTime?.budgetPace ?? (totalBudget > 0 ? (totalBudgetSpent / totalBudget) * 100 : 0));
  const spentToday = switchPacingYesterday
    ? pacingData?.budgetSpentYesterday || 0
    : pacingData?.budgetSpentToday || 0;
  const projection = classicPacing
    ? null
    : getAllTimeProjection({
        allTime,
        pacingData,
        todayEntry: paceAdditionalInfo?.[0],
        yesterday: switchPacingYesterday,
        isPercentage: true,
        projectToDate,
      });
  const paceColor = classicPacing
    ? getPaceColor(pace, true)
    : projection
      ? getPacingTone(projection.pace, { isPercentage: true })
      : getAllTimeTone(onPaceRatio);

  return (
    <>
      <Text style={sectionTitleStyle}>All Time Budget Pacing</Text>

      {!classicPacing && (
        <TabVisualSummary
          served={totalBudgetSpent}
          goal={totalBudget}
          dailyActual={spentToday}
          dailyGoal={expectedDaily}
          dailyLabel={switchPacingYesterday ? 'Spent yesterday' : 'Spent today'}
          byDate={row?.aggregateData?.byDate}
          highlightFromEnd={switchPacingYesterday ? 1 : 0}
          tone={paceColor}
          chartValueKey="totalCost"
          formatValue={(v) => formatCurrency2SigFig(v || 0)}
        />
      )}

      {projection && (
        <DetailRow
          label={projectToDate ? `Projected Finish by ${projectToDate}` : 'Projected Finish'}
          value={<span style={{ color: paceColor }}>{formatNumber2SigFig(projection.pace)}%</span>}
          tooltip={`${projectToDate ? `What-if end ${projectToDate}` : 'Full flight'}: (${switchPacingYesterday ? "yesterday's" : "today's"} spend × days left + spent) ÷ total budget${switchPacingYesterday ? '' : ' — today is a partial day'}`}
        />
      )}
      <DetailRow
        label={projection ? 'Spent vs Expected' : 'Pace'}
        value={<span style={{ color: projection ? getAllTimeTone(onPaceRatio) : paceColor }}>{formatNumber2SigFig(pace)}%</span>}
        tooltip={
          classicPacing
            ? 'Lifetime pace across the full flight'
            : 'Spent ÷ expected to date (budget × elapsed days ÷ total days) — ~100% = on pace'
        }
      />
      <DetailRow
        label="Remaining"
        value={formatCurrency2SigFig(remaining)}
        tooltip="Total Budget minus Total Budget Spent"
      />
      <DetailRow
        label="Expected Daily"
        value={formatCurrency2SigFig(expectedDaily)}
        tooltip="Total Budget divided by total flight days"
      />
      <DetailRow
        label={switchPacingYesterday ? 'Spent Yesterday' : 'Spent Today'}
        value={formatCurrency2SigFig(spentToday)}
        tooltip="Amount spent within the day"
      />
      <DetailRow
        label="Budget Spent"
        value={formatCurrency2SigFig(totalBudgetSpent)}
        tooltip="Total amount spent from the start of the campaign"
      />
      <DetailRow
        label="Total Budget"
        value={formatCurrency2SigFig(totalBudget)}
        tooltip="Total Budget for the whole duration of the campaign"
      />
    </>
  );
};

const ImpressionCurrent = ({ pacingData, row, status, switchPacingYesterday, paceAdditionalInfo, classicPacing }) => {
  const todayEntry = paceAdditionalInfo?.[0];
  const yesterdayEntry = paceAdditionalInfo?.[1];
  const source = switchPacingYesterday ? yesterdayEntry : todayEntry;

  let pace = source?.pace ?? 0;
  let impressionsLeft = source?.impressionsLeft ?? 0;
  let dailyImpressionsGoal = source?.dailyAvgImpressionsLeft ?? 0;
  let todaysImpressions = source?.impressionServedToday ?? 0;
  let impressionsSpent = source?.impressionsServed ?? 0;
  const impressionGoal = source?.impressionGoal ?? pacingData?.impressionGoal ?? 0;

  if (status === 'FINISHED') {
    impressionsSpent = row?.aggregateData?.overallData?.aggImpressions || impressionsSpent;
    dailyImpressionsGoal = 0;
    pace = todayEntry?.pace ?? pacingData?.pace ?? 0;
    todaysImpressions = 0;
    impressionsLeft = impressionGoal - impressionsSpent;
  }

  const paceColor = classicPacing ? getPaceColor(pace, false) : getPacingTone(pace);

  return (
    <>
      <Text style={sectionTitleStyle}>Current Impression Pacing</Text>

      {!classicPacing && status !== 'FINISHED' && (
        <TabVisualSummary
          served={impressionsSpent}
          goal={impressionGoal}
          dailyActual={todaysImpressions}
          dailyGoal={dailyImpressionsGoal}
          dailyLabel={switchPacingYesterday ? 'Impressions yesterday' : 'Impressions today'}
          byDate={row?.aggregateData?.byDate}
          highlightFromEnd={switchPacingYesterday ? 1 : 0}
          tone={paceColor}
          formatValue={(v) => formatWholeNumber(v || 0)}
        />
      )}

      <DetailRow
        label="Pace"
        value={<span style={{ color: paceColor }}>{pace !== null ? formatNumber2SigFig(pace) : 0}%</span>}
        tooltip={`Projected Pace. (Impression from ${switchPacingYesterday ? 'Yesterday' : 'Today'} * Days Remaining) + Served Impressions / Impression Goal`}
      />
      <DetailRow
        label={impressionsLeft < 0 ? 'Overage' : 'Remaining'}
        value={formatWholeNumber(impressionsLeft)}
      />
      {status !== 'FINISHED' && (
        <DetailRow
          label="Daily Goal"
          value={dailyImpressionsGoal ? formatWholeNumber(dailyImpressionsGoal) : '0'}
          tooltip="(Impression Goal - Served Impressions) / Days Remaining"
        />
      )}
      {status !== 'FINISHED' && (
        <DetailRow
          label="Impressions"
          value={todaysImpressions ? formatWholeNumber(todaysImpressions) : '0'}
          tooltip="Number of impressions served within the day"
        />
      )}
      <DetailRow
        label="Total Served Impressions"
        value={impressionsSpent ? formatWholeNumber(impressionsSpent) : '0'}
        tooltip="Cumulative impressions served as of the selected day"
      />
      <DetailRow
        label="Impression Goal"
        value={impressionGoal ? formatWholeNumber(impressionGoal) : '0'}
        tooltip="Impression goal for the current pacing period"
      />
    </>
  );
};

const ImpressionAllTime = ({ pacingData, row, switchPacingYesterday, paceAdditionalInfo, classicPacing, projectToDate }) => {
  const allTime = getAllTimeEntry(paceAdditionalInfo);
  const overallImpressions =
    allTime?.totalImpressionGoal ?? pacingData?.overallImpressions ?? 0;
  const overallDeliveredImpressions =
    allTime?.impressionsServed ?? pacingData?.overallDeliveredImressions ?? 0;
  const remaining = allTime?.impressionsLeft ?? (overallImpressions - overallDeliveredImpressions);
  const dailyGoal =
    allTime?.dailyAvgImpressions ??
    (() => {
      const flightDays = getFlightDays(row);
      return flightDays > 0 ? overallImpressions / flightDays : 0;
    })();
  // New view: transparent delivered-vs-expected-to-date ratio (same
  // formula as the hero) so the tab and hero always agree. Classic view
  // keeps the legacy backend number untouched.
  const onPaceRatio = classicPacing
    ? null
    : getAllTimeOnPaceRatio(
        overallImpressions,
        overallDeliveredImpressions,
        allTime?.daysIntoFlight,
        allTime?.daysLeft
      );
  const pace =
    onPaceRatio ??
    (allTime?.impressionPace ??
      (overallImpressions > 0 ? (overallDeliveredImpressions / overallImpressions) * 100 : 0));
  const todaysImpressions = switchPacingYesterday
    ? pacingData?.impressionsYesterday || 0
    : paceAdditionalInfo?.[0]?.impressionServedToday || 0;
  const projection = classicPacing
    ? null
    : getAllTimeProjection({
        allTime,
        pacingData,
        todayEntry: paceAdditionalInfo?.[0],
        yesterday: switchPacingYesterday,
        projectToDate,
      });
  const paceColor = classicPacing
    ? getPaceColor(pace, false)
    : projection
      ? getPacingTone(projection.pace)
      : getAllTimeTone(onPaceRatio);

  return (
    <>
      <Text style={sectionTitleStyle}>All Time Impression Pacing</Text>

      {!classicPacing && (
        <TabVisualSummary
          served={overallDeliveredImpressions}
          goal={overallImpressions}
          dailyActual={todaysImpressions}
          dailyGoal={dailyGoal}
          dailyLabel={switchPacingYesterday ? 'Impressions yesterday' : 'Impressions today'}
          byDate={row?.aggregateData?.byDate}
          highlightFromEnd={switchPacingYesterday ? 1 : 0}
          tone={paceColor}
          formatValue={(v) => formatWholeNumber(v || 0)}
        />
      )}

      {projection && (
        <DetailRow
          label={projectToDate ? `Projected Finish by ${projectToDate}` : 'Projected Finish'}
          value={<span style={{ color: paceColor }}>{formatNumber2SigFig(projection.pace)}%</span>}
          tooltip={`${projectToDate ? `What-if end ${projectToDate}` : 'Full flight'}: (${switchPacingYesterday ? "yesterday's" : "today's"} impressions × days left + delivered) ÷ impression goal${switchPacingYesterday ? '' : ' — today is a partial day'}`}
        />
      )}
      <DetailRow
        label={projection ? 'Delivered vs Expected' : 'Pace'}
        value={<span style={{ color: projection ? getAllTimeTone(onPaceRatio) : paceColor }}>{formatNumber2SigFig(pace)}%</span>}
        tooltip={
          classicPacing
            ? 'Lifetime pace across the full flight'
            : 'Delivered ÷ expected to date (goal × elapsed days ÷ total days) — ~100% = on pace'
        }
      />
      <DetailRow
        label={remaining < 0 ? 'Overage' : 'Remaining'}
        value={formatWholeNumber(remaining)}
        tooltip="Impression Goal minus Total Delivered"
      />
      <DetailRow
        label="Daily Goal"
        value={dailyGoal ? formatWholeNumber(dailyGoal) : '0'}
        tooltip="Impression Goal divided by total flight days"
      />
      <DetailRow
        label={switchPacingYesterday ? 'Yesterday' : 'Today'}
        value={todaysImpressions ? formatWholeNumber(todaysImpressions) : '0'}
        tooltip="Number of impressions served within the day"
      />
      <DetailRow
        label="Served Impressions"
        value={overallDeliveredImpressions ? formatWholeNumber(overallDeliveredImpressions) : '0'}
        tooltip="Number of impressions served from the start of the campaign"
      />
      <DetailRow
        label="Impression Goal"
        value={overallImpressions ? formatWholeNumber(overallImpressions) : '0'}
        tooltip="Total Impression goal for the whole duration of the campaign"
      />
    </>
  );
};

function getAllTimeEntry(paceAdditionalInfo) {
  if (!Array.isArray(paceAdditionalInfo)) return null;
  return paceAdditionalInfo.find((e) => e?.identifier === 'allTime') || null;
}

function getFlightDays(row) {
  if (!row?.startDate || !row?.endDate) return 0;
  const start = moment.utc(row.startDate);
  const end = moment.utc(row.endDate);
  if (!start.isValid() || !end.isValid()) return 0;
  // Inclusive day count: Aug 21 → Sep 20 is a 31-day flight, not 30.
  return Math.max(0, end.startOf('day').diff(start.startOf('day'), 'days') + 1);
}

const formatDateOnly = (value) => {
  if (!value) return '—';
  const m = moment.utc(value);
  return m.isValid() ? m.format('YYYY-MM-DD') : '—';
};

const ScheduleSection = ({ entry, pacingData, row }) => {
  const startDate = formatDateOnly(entry?.startDate ?? row?.startDate);
  const endDate = formatDateOnly(entry?.endDate ?? row?.endDate);
  const daysIntoFlight = entry?.daysIntoFlight ?? pacingData?.daysIntoFlight ?? '—';
  const daysLeft = entry?.daysLeft ?? pacingData?.daysLeft ?? '—';
  const dataRecorded = formatDateOnly(pacingData?.lastDataDate);

  return (
    <>
      <Divider style={{ margin: '12px 0' }} />
      <Text style={sectionTitleStyle}>Schedule</Text>

      <DetailRow
        label="Start Date"
        value={startDate}
        tooltip="Campaign flight start date"
      />
      <DetailRow
        label="End Date"
        value={endDate}
        tooltip="Campaign flight end date"
      />
      <DetailRow
        label="Days Into Flight"
        value={daysIntoFlight}
        tooltip="Days elapsed since the campaign start date"
      />
      <DetailRow
        label="Days Left"
        value={daysLeft}
        tooltip="Days remaining based on campaign end date"
      />
      <DetailRow
        label="Data Recorded"
        value={dataRecorded}
        tooltip="Date of the most recently recorded pacing data"
      />
    </>
  );
};

// Hero header for live campaigns: the projection at a glance — big number,
// progress bar (fills toward 100% with headroom to 125%), flight context,
// and the same ramping-window treatment as the table cell. Skipped for
// ended campaigns (final pacing, not a projection) and in classic mode.
const PacingHero = ({ pacingData, row, entry, todayEntry, switchPacingYesterday, allTime, projectToDate, onProjectToDateChange }) => {
  if (!pacingData) return null;
  // Source of truth: the flight-window entry from paceAdditionalInfo (same
  // as the table cell and the tab bodies). The scalar pacingData.pace /
  // daysIntoFlight / daysLeft can diverge (they sometimes reflect the
  // order's dates rather than this campaign's flight).
  const pace = entry?.pace ?? pacingData.pace;
  const daysIntoFlight = entry?.daysIntoFlight ?? pacingData.daysIntoFlight ?? 0;
  const daysLeft = entry?.daysLeft ?? pacingData.daysLeft ?? 0;
  const flightDays = getFlightDays(row);
  const flightLength = flightDays > 0 ? flightDays : daysIntoFlight + daysLeft;
  const unstableDays = Math.max(2, Math.min(5, Math.round(flightLength * 0.2)));
  // Ramping window is a current-flight concept; all-time is never "too young"
  const tooYoung = !allTime && daysIntoFlight > 0 && daysIntoFlight < unstableDays;
  const trend =
    pacingData.paceYesterday != null && pacingData.paceSevenDaysAgo != null
      ? pacingData.paceYesterday > pacingData.paceSevenDaysAgo
        ? 'rising'
        : pacingData.paceYesterday < pacingData.paceSevenDaysAgo
          ? 'falling'
          : 'steady'
      : null;
  // Visualization inputs (impression campaigns only): goal from
  // budget/CPM, delivered from the aggregate totals.
  const isPercentage = row?.pricingModel === 'Percentage';
  const cpm = row ? getEffectiveCpm(row) : null;
  const budget = row?.budget;
  const impressionGoal =
    !isPercentage && cpm && budget && budget !== 1 ? (budget / cpm) * 1000 : 0;
  const deliveredImpressions = row?.aggregateData?.overallData?.aggImpressions || 0;
  const byDate = row?.aggregateData?.byDate;
  // Delivery rate on the anchor day — not dailyAvgImpressions, which is the
  // planned daily average (goal ÷ flight days), not delivery.
  const ratePerDay = getDeliveryRatePerDay(pacingData, { yesterday: switchPacingYesterday, todayEntry });
  const impressionsLeft = switchPacingYesterday
    ? pacingData.impressionsLeftYesterday || 0
    : pacingData.impressionsLeft || 0;
  const neededPerDay = daysLeft > 0 ? impressionsLeft / daysLeft : 0;

  // All-time semantics: a POSITION ratio (delivered vs the pro-rata
  // expected-to-date), not a projection — computed via the shared helper
  // so the hero and the All Time tab never diverge.
  let allTimeOnPace = null; // delivered / expected-to-date, ~100 = on pace
  let allTimeDeliveredPct = null;
  let allTimeElapsedPct = null;
  let vizGoal = impressionGoal;
  let vizDeliveredPct = impressionGoal > 0 ? (deliveredImpressions / impressionGoal) * 100 : 0;
  // Full-flight projection (same formula as the current-flight number,
  // over the whole order), anchored on the selected day. Headline for the
  // All Time view; the position ratio moves to the line below it.
  const projection = allTime
    ? getAllTimeProjection({
        allTime: entry,
        pacingData,
        todayEntry,
        yesterday: switchPacingYesterday,
        isPercentage,
        projectToDate,
      })
    : null;
  if (allTime) {
    // Percentage campaigns are budget-paced: read budget fields, not
    // impressions, so the hero matches the All Time Budget tab below.
    const goal = isPercentage
      ? entry?.totalBudget ?? pacingData?.overallBudget ?? 0
      : entry?.totalImpressionGoal ?? pacingData?.overallImpressions ?? 0;
    const delivered = isPercentage
      ? entry?.budgetSpent ?? pacingData?.overallSpend ?? 0
      : entry?.impressionsServed ?? pacingData?.overallDeliveredImressions ?? 0;
    const totalDays = daysIntoFlight + daysLeft;
    allTimeOnPace = getAllTimeOnPaceRatio(goal, delivered, daysIntoFlight, daysLeft);
    allTimeDeliveredPct = goal > 0 ? (delivered / goal) * 100 : null;
    allTimeElapsedPct = totalDays > 0 ? (daysIntoFlight / totalDays) * 100 : null;
    vizGoal = goal;
    vizDeliveredPct = allTimeDeliveredPct ?? vizDeliveredPct;
  }
  const displayPace = allTime ? (projection?.pace ?? allTimeOnPace ?? pace) : pace;
  // Tones: projections (current flight and full flight) use the shared
  // getPacingTone gates (including the percentage >100 = overspend
  // inversion); the all-time position RATIO has its own gate — ~100 = on
  // pace, >120 = overdeliver/overspend — used only when no projection exists.
  const paceColor = allTime
    ? projection
      ? getPacingTone(projection.pace, { isPercentage })
      : getAllTimeTone(allTimeOnPace ?? pace)
    : getPacingTone(pace, { isPercentage, gracePeriod: daysIntoFlight < 10 });
  const positionColor = getAllTimeTone(allTimeOnPace);
  // Bar maps 0-125% so a healthy 100% projection still shows headroom
  const fill = tooYoung ? 0 : Math.max(0, Math.min(125, displayPace || 0)) / 1.25;
  const anchorLabel = switchPacingYesterday ? 'yesterday' : 'today';
  const partialDayNote = switchPacingYesterday ? '' : ' · today is a partial day, so this reads low until it closes';
  return (
    <div style={{ marginBottom: 16, width: '100%', overflow: 'hidden' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Text style={{ fontSize: 13, color: '#595959' }}>
          {allTime ? (projection ? 'Projected finish · full flight' : 'All-time · delivered vs expected to date') : 'Projected finish'}
        </Text>
        {!allTime && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Day {daysIntoFlight} of {flightLength}
          </Text>
        )}
        {allTime && projection && onProjectToDateChange && (
          // What-if end date: full goal kept, days recounted to the chosen
          // date. Nothing is saved; clearing returns to the order's end.
          <Tooltip title="What if the flight ended on this date? Keeps the full goal and recounts the days. Not saved.">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#595959' }}>
              Project to
              <DatePicker
                size="small"
                allowClear
                value={projectToDate ? moment.utc(projectToDate) : null}
                placeholder={entry?.endDate ? moment.utc(entry.endDate).format('YYYY-MM-DD') : 'Flight end'}
                format="YYYY-MM-DD"
                disabledDate={(d) => {
                  const latest = pacingData?.lastDataDate ? moment.utc(pacingData.lastDataDate) : null;
                  return !!latest && d && d.isSameOrBefore(latest, 'day');
                }}
                onChange={(d) => onProjectToDateChange(d ? d.format('YYYY-MM-DD') : null)}
                style={{ width: 128 }}
              />
            </span>
          </Tooltip>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4 }}>
        <Text
          style={{
            fontSize: 28,
            fontWeight: 600,
            lineHeight: 1.1,
            color: tooYoung ? '#595959' : PACING_TONES.ink,
            fontVariantNumeric: 'tabular-nums',
            display: 'inline-flex',
            alignItems: 'center',
          }}
        >
          {!tooYoung && <PacingDot tone={paceColor} />}
          {tooYoung ? '—' : `${formatNumber2SigFig(displayPace)}%`}
          {trend && !tooYoung && (
            <span style={{ marginLeft: 6, fontSize: 16, color: PACING_TONES.neutral, fontWeight: 400 }}>
              {TREND_ARROWS[trend]}
            </span>
          )}
        </Text>
      </div>
      <div style={{ position: 'relative', height: 6, background: '#f0f0f0', borderRadius: 3, marginTop: 8 }}>
        <div style={{ width: `${fill}%`, height: '100%', background: tooYoung ? PACING_TONES.neutral : paceColor, borderRadius: 3, transition: 'width 0.15s' }} />
        <div style={{ position: 'absolute', top: -1, bottom: -1, left: '80%', width: 1, backgroundColor: '#bfbfbf' }} />
      </div>
      {/* Bullet delivery bar (current flight): fill = delivered to date,
          tick = 100% of goal, marker = projected finish. In all-time mode
          there is no projection — a plain delivered-vs-goal bar. */}
      {vizGoal > 0 &&
        (allTime && !projection ? (
          <Tooltip title="Fill = delivered to date · gray tick = 100% of goal">
            <div style={{ marginTop: 10 }}>
              <PacingBar pace={vizDeliveredPct} tooYoung={false} tone={paceColor} height={6} />
            </div>
          </Tooltip>
        ) : (
          <Tooltip
            title={
              <div>
                Fill = delivered to date · gray tick = 100% of goal ·
                {' '}marker = projected finish ({tooYoung ? 'ramping' : `${formatNumber2SigFig(pace)}%`})
              </div>
            }
          >
            <div style={{ marginTop: 10 }}>
              <BulletBar deliveredPct={vizDeliveredPct} projectionPct={pace} tone={paceColor} height={6} />
            </div>
          </Tooltip>
        ))}
      {/* Delivering vs needed rate comparison (current-flight, impression
          campaigns only — for percentage campaigns the hero number is a
          budget %, so impression rate bars would contradict it) */}
      {!isPercentage && (!allTime || projection) && (
        <div style={{ marginTop: 10 }}>
          <RateBars
            delivering={allTime ? projection.ratePerDay : ratePerDay}
            needed={allTime ? projection.neededPerDay : neededPerDay}
            planned={allTime ? entry?.dailyAvgImpressions : pacingData.dailyAvgImpressions}
            tone={paceColor}
            labelWidth={70}
          />
        </div>
      )}
      {allTime && allTimeElapsedPct !== null ? (
        <>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
            {allTimeDeliveredPct !== null ? `${formatNumber2SigFig(allTimeDeliveredPct)}% of goal delivered` : '—'}
            {' · '}{formatNumber2SigFig(allTimeElapsedPct)}% of window elapsed
            {allTimeOnPace !== null && (
              <>
                {' · '}delivered vs expected to date{' '}
                <span style={{ color: positionColor, fontWeight: 600 }}>{formatNumber2SigFig(allTimeOnPace)}%</span>
              </>
            )}
          </Text>
          {projection && (
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 2 }}>
              {projection.daysAfter} day{projection.daysAfter === 1 ? '' : 's'} {projectToDate ? `to ${projectToDate}` : 'left'} · projection = ({anchorLabel}'s rate × days left + delivered) ÷ flight goal{partialDayNote}
            </Text>
          )}
        </>
      ) : (
        <>
          {tooYoung ? (
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
              Flight started
              {pacingData.pacingStartDate
                ? ' ' + moment(pacingData.pacingStartDate).format('MMM D')
                : ' recently'}
              {' · '}pacing estimate in {unstableDays - daysIntoFlight}d
            </Text>
          ) : (
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
              {daysLeft} day{daysLeft === 1 ? '' : 's'} left · projection = ({anchorLabel}'s rate × days left + delivered) ÷ flight goal{partialDayNote}
            </Text>
          )}
        </>
      )}
      {/* Cumulative delivered-vs-goal chart: the wedge between the
          delivered area and the dashed goal line is the projected
          shortfall/surplus. Impression campaigns only — the byDate series
          is impressions, which would contradict a budget headline. All-time
          uses the full-window goal; no projection line (position view). */}
      {vizGoal > 0 && !isPercentage && byDate && byDate.length > 1 && (
        <div style={{ marginTop: 12 }}>
          <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 2 }}>
            Delivered vs goal pace
          </Text>
          <CumulativePacingChart
            byDate={byDate}
            impressionGoal={vizGoal}
            flightLength={allTime ? daysIntoFlight + daysLeft : flightLength}
            pace={allTime ? projection?.pace ?? null : pace}
            flightStart={entry?.startDate ?? row?.startDate}
            flightEnd={entry?.endDate ?? row?.endDate}
            tone={paceColor}
          />
        </div>
      )}
    </div>
  );
};
const PacingDetailModal = ({
  visible,
  onClose,
  data,
  classicPacing,
}) => {
  const { pacingData, row, status, isPercentage, switchPacingYesterday, paceAdditionalInfo } = data || {};
  const currentEntry = switchPacingYesterday ? paceAdditionalInfo?.[1] : paceAdditionalInfo?.[0];
  const allTimeEntry = getAllTimeEntry(paceAdditionalInfo);
  // Hero follows the active tab; reset to Current whenever the modal opens
  const [activeTab, setActiveTab] = useState('current');
  // What-if end date for the All Time projection; reset with the modal
  const [projectToDate, setProjectToDate] = useState(null);
  useEffect(() => {
    if (visible) {
      setActiveTab('current');
      setProjectToDate(null);
    }
  }, [visible]);

  // Ended campaigns bypass the live pace/projection sources and render
  // from the same frontend helper the Campaigns table uses, keeping the
  // numbers consistent with the row.
  const isEnded = row && isEndedCampaign(row);
  const endedPacing = isEnded ? computeEndedPacing(row) : null;

  const renderCurrentBody = () => {
    if (endedPacing) {
      return isPercentage ? (
        <PercentageEnded ended={endedPacing} />
      ) : (
        <ImpressionEnded ended={endedPacing} />
      );
    }
    return isPercentage ? (
      <PercentageCurrent
        pacingData={pacingData}
        row={row}
        status={status}
        switchPacingYesterday={switchPacingYesterday}
        paceAdditionalInfo={paceAdditionalInfo}
        classicPacing={classicPacing}
      />
    ) : (
      <ImpressionCurrent
        pacingData={pacingData}
        row={row}
        status={status}
        switchPacingYesterday={switchPacingYesterday}
        paceAdditionalInfo={paceAdditionalInfo}
        classicPacing={classicPacing}
      />
    );
  };

  const renderAllTimeBody = () => {
    if (endedPacing) {
      return isPercentage ? (
        <PercentageEnded ended={endedPacing} />
      ) : (
        <ImpressionEnded ended={endedPacing} />
      );
    }
    return isPercentage ? (
      <PercentageAllTime
        pacingData={pacingData}
        row={row}
        switchPacingYesterday={switchPacingYesterday}
        paceAdditionalInfo={paceAdditionalInfo}
        classicPacing={classicPacing}
        projectToDate={projectToDate}
      />
    ) : (
      <ImpressionAllTime
        pacingData={pacingData}
        row={row}
        switchPacingYesterday={switchPacingYesterday}
        paceAdditionalInfo={paceAdditionalInfo}
        classicPacing={classicPacing}
        projectToDate={projectToDate}
      />
    );
  };

  return (
    <Modal
      title={`Pacing Details — ${row?.name || 'Campaign'}`}
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={520}
      bodyStyle={{ overflowX: 'hidden' }}
    >
      {!endedPacing && !classicPacing && (
        <PacingHero
          pacingData={pacingData}
          row={row}
          entry={activeTab === 'allTime' ? allTimeEntry : currentEntry}
          todayEntry={paceAdditionalInfo?.[0]}
          switchPacingYesterday={switchPacingYesterday}
          projectToDate={projectToDate}
          onProjectToDateChange={setProjectToDate}
          allTime={activeTab === 'allTime'}
        />
      )}
      <Divider style={{ margin: '0 0 12px 0' }} />
      {classicPacing || endedPacing ? (
        // Legacy tabs for classic view; ended campaigns keep the single
        // "Final Pacing" pane (a one-option switcher reads oddly).
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane
            tab={
              endedPacing
                ? 'Final Pacing'
                : `Current Pacing (${switchPacingYesterday ? 'Yesterday' : 'Today'})`
            }
            key="current"
          >
            {renderCurrentBody()}
            <ScheduleSection entry={currentEntry} pacingData={pacingData} row={row} />
          </TabPane>
          {!endedPacing && (
            <TabPane tab="All Time Pacing" key="allTime">
              {renderAllTimeBody()}
              <ScheduleSection entry={allTimeEntry} pacingData={pacingData} row={row} />
            </TabPane>
          )}
        </Tabs>
      ) : (
        // New view: pill-style switcher matching the Y/T + classic
        // controls in the table header — one design language everywhere.
        <>
          <Segmented
            value={activeTab}
            onChange={setActiveTab}
            style={{ marginBottom: 12 }}
            options={[
              {
                label: (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <AreaChartOutlined style={{ fontSize: 13 }} />
                    Current · {switchPacingYesterday ? 'Yesterday' : 'Today'}
                  </span>
                ),
                value: 'current',
              },
              {
                label: (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <HistoryOutlined style={{ fontSize: 13 }} />
                    All Time
                  </span>
                ),
                value: 'allTime',
              },
            ]}
          />
          {activeTab === 'current' ? (
            <>
              {renderCurrentBody()}
              <ScheduleSection entry={currentEntry} pacingData={pacingData} row={row} />
            </>
          ) : (
            <>
              {renderAllTimeBody()}
              <ScheduleSection entry={allTimeEntry} pacingData={pacingData} row={row} />
            </>
          )}
        </>
      )}
    </Modal>
  );
};

function getPaceColor(pace, isPercentage) {
  if (!pace) return '#ff4d4f';
  if (isPercentage) {
    if (pace <= 100 && pace >= 50) return '#52c41a';
    if (pace >= 0 && pace < 50) return '#faad14';
    return '#ff4d4f';
  }
  if (pace >= 95) return '#52c41a';
  if (pace >= 91 && pace <= 95) return '#faad14';
  return '#ff4d4f';
}

export default PacingDetailModal;
